import { Box } from '@mui/material';
import { ContactMail as ContactMailIcon, ContactPhone as ContactPhoneIcon } from '@mui/icons-material';
import { useMemo } from 'react';
import CellRow from 'src/careflow/common/CellRow';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { FhirResource } from 'src/careflow/ph-core/fhir-client/resources';
import IcomxInlineTelecomAction from 'src/sections/crs/patient/components/icomx/IcomxInlineTelecomAction';
import {
  getIcomxCandidateResourceDescriptor,
  mergeIcomxContactCandidates,
  type IcomxContactCandidate,
} from 'src/sections/crs/patient/components/icomx/icomxContactCandidateModel';
import { useReferenceIcomxCandidates } from 'src/sections/crs/patient/components/icomx/useIcomxContactCandidates';

interface TaskOwnerInlineTelecomCellProps {
  ownerName: string;
  ownerReference?: string | null;
  ownerResourceType?: FhirResource['resourceType'] | null;
  ownerResourceId?: string | null;
  ownerPhone?: string | null;
  ownerEmail?: string | null;
}

export default function TaskOwnerInlineTelecomCell({
  ownerName,
  ownerReference,
  ownerResourceType,
  ownerResourceId,
  ownerPhone,
  ownerEmail,
}: TaskOwnerInlineTelecomCellProps) {
  const { i18n } = useLocales();
  const resolvedReference =
    ownerReference ?? (
      ownerResourceType && ownerResourceId
        ? `${ownerResourceType}/${ownerResourceId}`
        : null
    );
  const seededCandidates = useMemo<IcomxContactCandidate[]>(() => {
    if (!ownerResourceType || !ownerResourceId || (!ownerPhone && !ownerEmail)) {
      return [];
    }

    return [
      {
        id: `${ownerResourceType}/${ownerResourceId}`,
        label: ownerName || 'Task Owner',
        resourceType: ownerResourceType,
        resourceId: ownerResourceId,
        firstName: ownerName || 'Task',
        lastName: 'Owner',
        phone: ownerPhone ?? undefined,
        email: ownerEmail ?? undefined,
        telecomSummary: [ownerPhone, ownerEmail].filter((value): value is string => !!value),
        sourceLabel: 'Task Owner',
      },
    ];
  }, [ownerEmail, ownerName, ownerPhone, ownerResourceId, ownerResourceType]);
  const { candidates: fetchedCandidates } = useReferenceIcomxCandidates(resolvedReference, 'Task Owner');
  const candidates = useMemo(
    () => mergeIcomxContactCandidates([...seededCandidates, ...fetchedCandidates]),
    [fetchedCandidates, seededCandidates]
  );
  const phoneCandidate = candidates.find((candidate) => {
    const resource = getIcomxCandidateResourceDescriptor(candidate);
    return !!candidate.phone && !!resource;
  }) ?? null;
  const emailCandidate = candidates.find((candidate) => {
    const resource = getIcomxCandidateResourceDescriptor(candidate);
    return !!candidate.email && !!resource;
  }) ?? null;
  const phoneResource = getIcomxCandidateResourceDescriptor(phoneCandidate);
  const emailResource = getIcomxCandidateResourceDescriptor(emailCandidate);

  return (
    <Box>
      <CellRow shouldTruncateText={false} title={ownerName} />
      {phoneCandidate?.phone ? (
        <CellRow
          tooltipTitle={i18n('patients.phone', 'crs')}
          shouldTruncateText={false}
          title={phoneCandidate.phone}
          Icon={<ContactPhoneIcon fontSize="small" />}
          endAdornment={
            <IcomxInlineTelecomAction
              candidates={candidates}
              resourceType={phoneResource?.resourceType}
              resourceId={phoneResource?.resourceId}
              system="phone"
              tooltip="Open task owner phone in iComx"
              value={phoneCandidate.phone}
            />
          }
        />
      ) : null}
      {emailCandidate?.email ? (
        <CellRow
          tooltipTitle={i18n('patients.email', 'crs')}
          shouldTruncateText={false}
          title={emailCandidate.email}
          Icon={<ContactMailIcon fontSize="small" />}
          endAdornment={
            <IcomxInlineTelecomAction
              candidates={candidates}
              resourceType={emailResource?.resourceType}
              resourceId={emailResource?.resourceId}
              system="email"
              tooltip="Open task owner email in iComx"
              value={emailCandidate.email}
            />
          }
        />
      ) : null}
    </Box>
  );
}
